import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  HStack,
  VStack,
  Button,
  Link,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiFileText, FiShield, FiMail, FiArrowRight } from 'react-icons/fi';
import { PLATFORM_LIST } from './content/platforms';
import PlatformIcon from './components/PlatformIcon';
import { useLegalLang } from './LegalLangContext';
import { APP_META } from './content/appMeta';

const PlatformCard = ({ platform }) => {
  const { t } = useLegalLang();

  return (
    <Box
      position="relative"
      bg="surface.base"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      p={5}
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        borderColor: 'brand.300',
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 32px rgba(79, 70, 229, 0.10)',
      }}
      overflow="hidden"
    >
      {/* accent stripe theo màu platform */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="3px"
        bgGradient={platform.gradient}
      />

      <Flex align="center" gap={3} mb={4}>
        <Flex
          boxSize="44px"
          borderRadius="12px"
          bg="gray.50"
          border="1px solid"
          borderColor="gray.100"
          align="center"
          justify="center"
        >
          <PlatformIcon platform={platform} size={22} />
        </Flex>
        <Box>
          <Text fontWeight="700" fontSize="md" color="text.primary" lineHeight="1.2">
            {platform.name}
          </Text>
          <Text fontSize="xs" color="text.muted" mt="2px">
            {platform.oauthProvider}
          </Text>
        </Box>
      </Flex>

      {/* Stack vertical để text dài (vd. tiếng Việt "Điều khoản Sử dụng") không bị tràn card */}
      <VStack spacing={2} mt={3} align="stretch">
        <Button
          as={RouterLink}
          to={`/legal/${platform.slug}/terms`}
          size="sm"
          variant="outline"
          borderColor="gray.200"
          color="text.primary"
          fontWeight="500"
          leftIcon={<FiFileText size={14} />}
          _hover={{ borderColor: 'brand.500', color: 'brand.700', bg: 'brand.50' }}
          justifyContent="flex-start"
        >
          {t('viewTerms')}
        </Button>
        <Button
          as={RouterLink}
          to={`/legal/${platform.slug}/privacy`}
          size="sm"
          variant="outline"
          borderColor="gray.200"
          color="text.primary"
          fontWeight="500"
          leftIcon={<FiShield size={14} />}
          _hover={{ borderColor: 'brand.500', color: 'brand.700', bg: 'brand.50' }}
          justifyContent="flex-start"
        >
          {t('viewPrivacy')}
        </Button>
      </VStack>
    </Box>
  );
};

const LegalIndex = () => {
  const { t, lang } = useLegalLang();

  const dateStr = new Date(APP_META.effectiveDate).toLocaleDateString(
    lang === 'vi' ? 'vi-VN' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  return (
    <Box>
      {/* Hero */}
      <Box
        position="relative"
        overflow="hidden"
        pt={{ base: 10, md: 16 }}
        pb={{ base: 10, md: 14 }}
      >
        <Box
          position="absolute"
          inset={0}
          bgGradient="radial(ellipse at top, rgba(99, 102, 241, 0.10), transparent 60%)"
          pointerEvents="none"
        />
        <Container maxW="6xl" px={{ base: 4, md: 6 }} position="relative">
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            py={1}
            bg="brand.50"
            border="1px solid"
            borderColor="brand.100"
            borderRadius="full"
            mb={4}
          >
            <Box boxSize="8px" borderRadius="full" bg="brand.500" />
            <Text fontSize="xs" fontWeight="600" color="brand.700">
              {APP_META.name}
            </Text>
          </Box>
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="700"
            color="text.primary"
            lineHeight="1.1"
            letterSpacing="-0.02em"
            maxW="3xl"
          >
            {t('indexTitle')}
          </Heading>
          <Text
            mt={4}
            fontSize={{ base: 'md', md: 'lg' }}
            color="text.secondary"
            maxW="3xl"
            lineHeight="1.7"
          >
            {t('indexSubtitle')}
          </Text>
          <Text mt={3} fontSize="sm" color="text.muted">
            {t('effectiveDateLabel')}:{' '}
            <Text as="span" color="text.secondary" fontWeight="500">
              {dateStr}
            </Text>
          </Text>
        </Container>
      </Box>

      <Container maxW="6xl" px={{ base: 4, md: 6 }} pb={16}>
        {/* Section title */}
        <Flex direction="column" mb={6}>
          <Heading as="h2" fontSize="xl" fontWeight="700" color="text.primary">
            {t('perPlatformTitle')}
          </Heading>
          <Text mt={2} color="text.secondary" fontSize="sm" maxW="3xl">
            {t('perPlatformDescription')}
          </Text>
        </Flex>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
          {PLATFORM_LIST.map(p => (
            <PlatformCard key={p.slug} platform={p} />
          ))}
        </SimpleGrid>

        <Divider my={12} borderColor="gray.200" />

        {/* Contact */}
        <Box
          bg="gray.50"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="xl"
          p={{ base: 5, md: 6 }}
        >
          <Flex
            align={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            justify="space-between"
          >
            <Box>
              <HStack spacing={2} mb={2}>
                <FiMail size={16} color="#4F46E5" />
                <Text fontWeight="700" color="text.primary">
                  {t('contactUs')}
                </Text>
              </HStack>
              <Text color="text.secondary" fontSize="sm">
                {t('contactPrompt')} {t('emailUs')}{' '}
                <Link
                  href={`mailto:${APP_META.contactEmail}`}
                  color="brand.700"
                  fontWeight="600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  {APP_META.contactEmail}
                </Link>
                .
              </Text>
            </Box>
            <Button
              as="a"
              href={`mailto:${APP_META.contactEmail}`}
              colorScheme="brand"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
              size="md"
              rightIcon={<FiArrowRight size={14} />}
            >
              {APP_META.contactEmail}
            </Button>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalIndex;
